import React, { useEffect, useMemo, useState } from 'react';
import { ListChecks, Play, CalendarCheck, CalendarClock, AlertTriangle, Layers, Boxes } from 'lucide-react';
import { getCadenceInsights, CadenceInsights as CadenceInsightsData } from '../../services/cadenceService';
import Toast, { type ToastMessage } from '../common/Toast';
import { CadenceInsightsSkeleton } from '../common/InsightsSkeleton';
import NoDataFound from '../common/NoDataFound';
import { usePermission } from '../../contexts/PermissionContext';
import { useEntitySettings } from '../../contexts/EntitySettingsContext';

const ACT_INSIGHTS = 'SCH_GLOBAL_CADENCE';

const EMPTY: CadenceInsightsData = {
    rules_total: 0, rules_active: 0, runs_total: 0, runs_scheduled: 0,
    last_run_at: null, last_run: null, firmed_upcoming: 0,
    tentative_upcoming: 0, upcoming_instances: 0,
    rules_by_status: [], rules_by_module: [], instances_by_status: [],
};

const MODULE_COLORS = ['#6366f1', '#3b82f6', '#06b6d4', '#10b981', '#f59e0b', '#f97316', '#8b5cf6', '#94a3b8'];
const STATUS_COLORS: Record<string, string> = { active: '#10b981', inactive: '#94a3b8', paused: '#f59e0b', draft: '#6366f1' };
const INSTANCE_COLORS: Record<string, string> = { firmed: '#10b981', tentative: '#f59e0b', cancelled: '#ef4444', completed: '#6366f1' };

const KpiCard = ({ label, value, sub, icon: Icon, iconBg, iconColor }: any) => (
    <div className="relative bg-white rounded-2xl flex items-center gap-4 px-5 py-5 cursor-default overflow-hidden"
        style={{ border: '1px solid #e8ecf4', boxShadow: '0 4px 20px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)', transition: 'box-shadow 0.2s ease, transform 0.2s ease' }}
        onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.boxShadow = '0 8px 28px rgba(0,0,0,0.11), 0 2px 6px rgba(0,0,0,0.06)'; (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-1px)'; }}
        onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 20px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)'; (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)'; }}>
        {iconColor && <div className="absolute bottom-0 right-0 w-24 h-24 rounded-full pointer-events-none" style={{ background: `radial-gradient(circle, ${iconColor}0d 0%, transparent 70%)`, transform: 'translate(30%, 30%)' }} />}
        <div className="shrink-0 p-3 rounded-xl" style={{ background: iconBg, boxShadow: `0 2px 10px ${iconColor}28` }}>
            <Icon size={20} style={{ color: iconColor }} />
        </div>
        <div className="min-w-0 flex-1">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 truncate mb-1">{label}</p>
            <span className="text-[2rem] font-extrabold text-slate-800 leading-none tabular-nums">{value}</span>
            {sub && <p className="text-[10px] text-slate-400 mt-1 truncate">{sub}</p>}
        </div>
    </div>
);

const DistributionWidget = ({ title, data, icon: Icon, total, accentColor = '#6366f1' }: {
    title: string; data: { label: string; count: number; color: string }[];
    icon: any; total: number; accentColor?: string;
}) => {
    const maxCount = Math.max(...data.map(d => d.count), 1);
    return (
        <div className="rounded-2xl overflow-hidden flex flex-col"
            style={{ background: '#ffffff', border: '1px solid #e8ecf4', boxShadow: '0 4px 20px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)' }}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-white">
                <div className="flex items-center gap-2">
                    <div className="p-1.5 rounded-lg" style={{ background: `${accentColor}12` }}>
                        <Icon size={12} style={{ color: accentColor }} />
                    </div>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">{title}</span>
                </div>
                {total > 0 && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full tabular-nums" style={{ background: `${accentColor}12`, color: accentColor }}>{total}</span>}
            </div>
            {data.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-8 italic">No data yet.</p>
            ) : (
                <ul className="divide-y divide-slate-50 flex-1">
                    {data.map((item, idx) => {
                        const barPct = item.count > 0 ? Math.round((item.count / maxCount) * 100) : 0;
                        const pct = total > 0 ? Math.round((item.count / total) * 100) : 0;
                        return (
                            <li key={idx} className="grid items-center gap-3 px-4 py-2.5 transition-colors duration-100"
                                style={{ gridTemplateColumns: '1fr 110px 3.5rem' }}
                                onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                                <span className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide truncate">{item.label}</span>
                                <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'linear-gradient(to bottom, #f1f5f9, #e2e8f0)' }}>
                                    <div className="h-1.5 rounded-full transition-all duration-500"
                                        style={{ width: barPct > 0 ? `${barPct}%` : '0%', background: `linear-gradient(90deg, ${item.color}bb, ${item.color})`, boxShadow: barPct > 10 ? `0 1px 4px ${item.color}55` : 'none' }} />
                                </div>
                                <div className="flex items-center justify-end gap-1">
                                    <span className="text-[12px] font-extrabold tabular-nums" style={{ color: item.count > 0 ? item.color : '#94a3b8' }}>{item.count}</span>
                                    <span className="text-[10px] text-slate-400">({pct}%)</span>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

const CadenceInsights: React.FC = () => {
    const [data, setData] = useState<CadenceInsightsData>(EMPTY);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState<ToastMessage | null>(null);
    const { allowed: canView, loading: viewLoading } = usePermission(ACT_INSIGHTS, 'view');
    const { formatDateTime } = useEntitySettings();
    const FMT = (s?: string | null) => (s ? formatDateTime(s) : '—');

    useEffect(() => {
        if (!canView) return;
        let cancelled = false;
        setLoading(true);
        getCadenceInsights()
            .then(d => { if (!cancelled && d) setData(d); })
            .catch(e => { if (!cancelled) setToast({ type: 'error', message: e?.response?.data?.detail || e?.message || 'Failed to load cadence insights' }); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [canView]);

    const lr = data.last_run;
    const moduleData = useMemo(() => data.rules_by_module.map((r, i) => ({ label: r.label, count: r.count, color: MODULE_COLORS[i % MODULE_COLORS.length] })), [data.rules_by_module]);
    const statusData = useMemo(() => data.rules_by_status.map(r => ({ label: r.label, count: r.count, color: STATUS_COLORS[r.label.toLowerCase()] || '#94a3b8' })), [data.rules_by_status]);
    const instanceData = useMemo(() => data.instances_by_status.map(r => ({ label: r.label, count: r.count, color: INSTANCE_COLORS[r.label.toLowerCase()] || '#94a3b8' })), [data.instances_by_status]);
    const moduleTotal = useMemo(() => moduleData.reduce((s, r) => s + r.count, 0), [moduleData]);
    const statusTotal = useMemo(() => statusData.reduce((s, r) => s + r.count, 0), [statusData]);
    const instanceTotal = useMemo(() => instanceData.reduce((s, r) => s + r.count, 0), [instanceData]);

    if (!viewLoading && !canView) {
        return (
            <div className="flex flex-col h-full min-h-[300px] items-center justify-center">
                <NoDataFound title="Not authorised" message="You do not have access to cadence insights." />
            </div>
        );
    }

    return (
        <div className="netbody-card flex flex-col h-full min-h-[500px]">
            <div className="netbody-content">
                <div className="scroll-container-nofilters flex-1 overflow-hidden">
                    {loading && <CadenceInsightsSkeleton />}
                    {!loading && (
                        <div className="py-4 space-y-4">
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                <KpiCard label="Cadence Rules" value={data.rules_total} sub={`${data.rules_active} active`} icon={ListChecks} iconBg="#eff6ff" iconColor="#3b82f6" />
                                <KpiCard label="Engine Runs" value={data.runs_total} sub={`${data.runs_scheduled} scheduled · last ${FMT(data.last_run_at)}`} icon={Play} iconBg="#f5f3ff" iconColor="#8b5cf6" />
                                <KpiCard label="Obligations Last Run" value={lr ? lr.instances_created : 0} sub={lr ? `${lr.instances_skipped} skipped · ${lr.status ?? ''}` : 'No runs yet'} icon={CalendarCheck} iconBg="#ecfdf5" iconColor="#10b981" />
                                <KpiCard label="Upcoming Firmed-up" value={data.firmed_upcoming} sub={`${data.tentative_upcoming} still tentative`} icon={CalendarClock} iconBg="#fffbeb" iconColor="#f59e0b" />
                            </div>
                            {lr && lr.rules_failed > 0 && (
                                <div className="px-3 py-2 bg-red-50 border border-red-100 rounded-xl flex gap-2 items-center" style={{ boxShadow: '0 1px 4px rgba(239,68,68,0.08)' }}>
                                    <AlertTriangle size={13} className="text-red-500 shrink-0" />
                                    <p className="text-xs text-red-700">Last run ({lr.trigger_source}) had <span className="font-semibold">{lr.rules_failed}</span> rule failure{lr.rules_failed === 1 ? '' : 's'} out of {lr.rules_evaluated} evaluated.</p>
                                </div>
                            )}
                            <div className="grid grid-cols-3 gap-4">
                                <DistributionWidget title="Rules by Module" data={moduleData} icon={Boxes} total={moduleTotal} accentColor="#3b82f6" />
                                <DistributionWidget title="Rules by Status" data={statusData} icon={Layers} total={statusTotal} accentColor="#10b981" />
                                <DistributionWidget title="Obligations by Status" data={instanceData} icon={CalendarCheck} total={instanceTotal} accentColor="#f59e0b" />
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <Toast toast={toast} onClose={() => setToast(null)} />
        </div>
    );
};

export default CadenceInsights;
export { CadenceInsights };
